import React, { FC, ReactNode, useEffect, useState } from 'react';
import {
  BaseModal,
  Button,
  Form,
  FormField,
  Input,
  Multiselect,
  ToastMessage,
  formatPhoneNumber
} from '@cokitchen/cokitchen-components';
import { MultiselectItem } from '@cokitchen/cokitchen-components/dist/form/multiselect/types';
import { ENUM_INBOUND_ROLE } from 'utils/enum';
import { AddIcon } from 'cokitchen-icons';
import { UserType } from 'models/user.model';
import { apiService } from 'utils/api';
import { toast } from 'react-toastify';
import '../styles.scss';
import { CoKitchenType } from 'models/cokitchen.model';
import { STORAGE_KEYS } from 'utils/constants';
import { useForm } from 'react-hook-form';
import { CSRepFormType, repSchema } from './utils';
import { yupResolver } from '@hookform/resolvers/yup';

type NewRepModalProps = {
  show: boolean;
  type?: 'create' | 'edit';
  children?: ReactNode;
  onClose: (refetchData?: boolean) => void;
  singleRep?: UserType | null;
};

interface NewRepFooterProps {
  loading: boolean;
  onConfirm: () => void;
  disabled: boolean;
}

const NewRepFooter: FC<NewRepFooterProps> = ({ onConfirm, loading, disabled }) => {
  return (
    <div>
      <Button
        small
        color='primary'
        variant='block'
        className='mx-auto'
        loading={loading}
        disabled={disabled}
        onClick={onConfirm}>
        Confirm
      </Button>
    </div>
  );
};

const NewRepModal = ({ show, onClose, singleRep, type = 'create' }: NewRepModalProps) => {
  const form = useForm<CSRepFormType>({
    resolver: yupResolver(repSchema),
    defaultValues: {
      cokitchenId: singleRep?.cokitchen_id,
      email: singleRep?.email,
      firstName: singleRep?.first_name as string,
      lastName: singleRep?.last_name as string,
      phoneNumber: singleRep?.phone_number,
      role: singleRep?.role as ENUM_INBOUND_ROLE
    }
  });

  const [loading, setLoading] = useState(false);
  const [rolesList, setRolesList] = useState<string[]>([]);

  const cokitchenArray: CoKitchenType[] = JSON.parse(
    localStorage.getItem(STORAGE_KEYS.COKITCHEN_LIST) || '[]'
  );

  const createRep = (data: CSRepFormType) => {
    if (type === 'create') {
      handleCreateRep(data);
    } else {
      handleEditRep(data);
    }
  };

  useEffect(() => {
    getAllRoles();
  }, []);

  const getAllRoles = async () => {
    const res = await apiService.get('cs/roles/all/list', {
      baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
    });

    setRolesList(res.data?.data);
  };

  const handleEditRep = async (data: CSRepFormType) => {
    try {
      setLoading(true);
      const res = await apiService.post('cs/staff/edit', {
        data: {
          user_id: singleRep?.id,
          role: data.role.toUpperCase()
        },
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      if (res) {
        toast(<ToastMessage type='success' message='Cs rep successful updated' />);
        onClose(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRep = async (data: CSRepFormType) => {
    try {
      setLoading(true);
      const res = await apiService.post('cs/staff/add', {
        data: {
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          cokitchen_id: data.cokitchenId,
          phone_number: formatPhoneNumber(String(data?.phoneNumber)),
          role: data.role.toUpperCase()
        },
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      if (res) {
        toast(<ToastMessage type='success' message='Cs rep successful created' />);
        onClose(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const roles: MultiselectItem[] = rolesList?.map((role) => {
    return {
      text: role.split('_').join(' ').toLocaleLowerCase(),
      value: role
    };
  });

  return (
    <BaseModal
      isOpen={show}
      title=''
      titleIcon={<AddIcon className='text-blue-5' />}
      titleIconColor='info'
      onRequestClose={() => {
        onClose();
      }}
      footer={
        <NewRepFooter
          onConfirm={form.handleSubmit(createRep)}
          loading={loading}
          disabled={!form.formState.isValid}
        />
      }>
      <Form form={form} onSubmit={createRep}>
        <FormField
          control={form.control}
          name='cokitchenId'
          render={({ field, fieldState: { error } }) => (
            <div>
              <p className='font-medium text-sm'>Select Cokitchen</p>
              <Multiselect
                isSingle
                placeholder='Select location'
                values={[field.value]}
                options={cokitchenArray.map((p) => {
                  return {
                    text: p.name,
                    value: p.id
                  };
                })}
                onChange={(e) => field.onChange(e?.[0])}
              />
              {error && (
                <span className='text-red-5 block font-medium text-xs mt-1'>{error?.message}</span>
              )}
            </div>
          )}
        />

        <div className='flex gap-[22px] mt-6'>
          <FormField
            name='firstName'
            control={form.control}
            render={({ field, fieldState: { error } }) => (
              <>
                <Input
                  {...field}
                  label='First Name'
                  small
                  inputClass='placeholder:text-sm'
                  className='text-[14px] '
                  placeholder='Paul'
                  error={error?.message}
                />
              </>
            )}
          />

          <FormField
            name='lastName'
            control={form.control}
            render={({ field, fieldState: { error } }) => (
              <>
                <Input
                  {...field}
                  label='Last Name'
                  small
                  inputClass='placeholder:text-sm'
                  className='text-[14px] '
                  placeholder='Nathan'
                  error={error?.message}
                />
              </>
            )}
          />
        </div>

        <div className='pt-[16px]'>
          <FormField
            name='phoneNumber'
            control={form.control}
            render={({ field, fieldState: { error } }) => (
              <>
                <Input
                  {...field}
                  label='Phone number'
                  type='phone'
                  small
                  inputClass='placeholder:text-sm'
                  className='text-[14px] '
                  placeholder='07015671251'
                  error={error?.message}
                />
              </>
            )}
          />
        </div>

        <div className='pt-[16px]'>
          <FormField
            name='email'
            control={form.control}
            render={({ field, fieldState: { error } }) => (
              <>
                <Input
                  {...field}
                  label='Email'
                  small
                  inputClass='placeholder:text-sm'
                  className='text-[14px]'
                  placeholder='e.g Paulnathan12@getfoodcourt.com'
                  autoComplete='@getfoodcourt.com'
                  error={error?.message}
                />
              </>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name='role'
          render={({ field, fieldState: { error } }) => (
            <div className='pt-[16px] pb-[10px] text-[14px] new_rep'>
              <p className='font-medium text-sm'>Assign role</p>
              <Multiselect
                isSingle
                placeholder='Select role'
                values={[field.value]}
                options={roles}
                onChange={(e) => field.onChange(e?.[0])}
              />
              {error && (
                <span className='text-red-5 block font-medium text-xs mt-1'>{error?.message}</span>
              )}
            </div>
          )}
        />
      </Form>
    </BaseModal>
  );
};

export default NewRepModal;
